import { useCallback, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { MapCardMarker } from "types/map";
import { registerReferenceCardMarker } from "redux/slices/shed/shedSlice";
import { useAppDispatch } from "redux/hooks";
import { getMarkerCardImageSrc, getMarkerIconImageSrc } from "libraries/map";

type Props = {
  map: google.maps.Map | null; // Map instance passed from MapContainer
  onCardClick: (cardMarker: MapCardMarker) => void;
  marker: MapCardMarker;
};

const MARKER_IMAGE_MIN_WIDTH = 160;

const MapMarkerCard = ({ map, marker, onCardClick }: Props) => {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const circleRef = useRef<google.maps.Circle | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();
  const theme = useTheme();

  const handleMarkerClick = useCallback(() => {
    console.log("[Map] Experience Card marker click", marker.name);
    dispatch(registerReferenceCardMarker(marker));
    onCardClick(marker);

    // Consider adding "registerReferenceExperience"
  }, [onCardClick, dispatch, marker]);

  useEffect(() => {
    if (!map) return;

    // Create the HTML content for the AdvancedMarkerElement
    if (!contentRef.current) {
      contentRef.current = document.createElement("div");
      const isFrenchFlagMarker = marker.experienceId === "my-little-france";
      const imageSrc = isFrenchFlagMarker
        ? getMarkerIconImageSrc({
            experienceId: marker.experienceId,
            lat: marker.lat,
            lng: marker.lng,
            name: marker.name,
            circleRadius: marker.circleRadius,
          })
        : getMarkerCardImageSrc(marker);
      const imageWidth = isFrenchFlagMarker ? 40 : MARKER_IMAGE_MIN_WIDTH;

      contentRef.current.innerHTML = `<img style="box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);" width=${imageWidth} src="${imageSrc}" alt="${marker.name}" />`;
    }

    // Create marker only once
    if (!markerRef.current) {
      markerRef.current = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: {
          lat: marker.lat,
          lng: marker.lng,
        },
        content: contentRef.current,
        title: marker.name,
      });

      markerRef.current.addListener("click", handleMarkerClick);
    }

    if (!circleRef.current && marker.circleRadius > 0) {
      circleRef.current = new google.maps.Circle({
        map,
        radius: marker.circleRadius,
        strokeWeight: 2,
        strokeColor: theme.palette.success.main,
        fillColor: theme.palette.success.main,
        fillOpacity: 0.2,
        center: {
          lat: marker.lat,
          lng: marker.lng,
        },
      });
    }

    return () => {
      // Cleanup: Remove content and marker from the map
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current = null;
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [map]);

  return null;
};

export default MapMarkerCard;
